import React from 'react'

export interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

const isDevelopment = typeof process !== 'undefined' && process.env.NODE_ENV === 'development'

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('🚨 Error caught by ErrorBoundary:', error)
    console.error('📋 Error Info:', errorInfo)
    this.setState({ errorInfo })
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-slate-900 p-4">
          <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
            <div className="text-red-500 text-5xl mb-4 text-center">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2 text-center">خطا رخ داد</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {this.state.error?.message || 'خطایی نامشخص رخ داد'}
            </p>
            {isDevelopment && this.state.errorInfo && (
              <details className="mb-4 p-3 bg-gray-100 dark:bg-slate-700 rounded text-sm text-gray-600 dark:text-gray-300">
                <summary className="font-semibold cursor-pointer mb-2">جزئیات توسعه‌دهنده</summary>
                <pre className="whitespace-pre-wrap break-words text-xs overflow-auto max-h-40">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            <button
              onClick={this.handleReset}
              className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors font-semibold"
            >
              سعی دوباره
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
